
/**
 *
 * Knowledge Page
 *
 */

import React, { 
  useEffect, 
  useState, 
  useRef,
  useContext,
} from 'react';
import { Helmet } from 'react-helmet';
import { Query } from 'react-apollo';
import CSVDownloader from 'react-csv-downloader';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from '@pearsontechnology/aero-components';
import mercuryContext from 'ConnectionManager/mercuryContext';
import helpContext from 'constants/helpContext';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import PlaceholderList from 'components/PlaceholderList';
import { pushToast } from 'containers/ToastManager/actions';
import { commandWorkspaceWorkspacesList } from 'containers/ConnectionManager/actions';
import { dispatch } from 'pluginServices/store';
import {
  PageWrapper,
  PageViewport,
  PageContent,
  PageActions,
  PageActionsRight,
} from 'components/PageStyles';
import Sidebar from 'Workspace/components/WorkspaceSidebar';
import HomeNavigation from 'Workspace/components/HomeNavigation';
import WorkspaceHeader from 'Workspace/components/WorkspaceHeader';
import CourseSelectorModal from 'Workspace/components/CourseSelectorModal';
import { getKnowledgeMapDetail, deleteKnowledgeMap, createKnowledgeMap } from 'Workspace/Knowledge/services';
import getDocumentsList from 'Workspace/queries/getDocumentsList';
import HelpMenu from 'Workspace/components/WorkspaceHelpMenu';
import knowledgeMapCsvTemplatePath from 'Workspace/Knowledge/assets/KnowledgeMap_Template.csv';
import knowledgeMapDocumentationPdfPath from 'Workspace/Knowledge/assets/KnowledgeMap_Documentation.pdf';
import { 
  getCompetencyList,
  CSVHeaderNames,
  getKnowledgeMapFileName,
  insertKnowledgeMapItems,
  insertKnowledgeMapAssociation,
} from 'Workspace/Knowledge/utils';
import { updateKnowledgeMapItemTitle } from 'Workspace/Knowledge/utils/uploadPrefix';
import KnowledgeMapDownload from 'Workspace/Knowledge/components/KnowledgeMapDownload';
import WorkspaceService from 'services/WorkspaceService';
import gtmService from 'utils/gtm/gtmService';
import { 
  KnowledgeMapDownloadOptions,
  KnowledgeTypes, 
  KnowledgeMapCsvTemplateFileName, 
  knowledgeMapPdfDocumentationFileName, 
  KnowledgeMapFilenamePrefix,
} from 'constants/knowledgeMap';
import simplifyDocumentQueryResult, { simplifyKnowledgeMapQueryResult, addDeleteProperty } from 'builder/Knowledge/helpers/simplifyDocumentQueryResult';
import { selectCurrentMe } from 'data/CurrentUser/selectors';
import KnowledgeMapsList from '../KnowledgeMapsList';
import AddKnowledgeMap from '../AddKnowledgeMap';
import KnowledgeMapImportModal from '../KnowledgeMapImportModal';
import KnowledgeMapDeleteConfirm from '../KnowledgeMapDeleteConfirm';
import messages from './messages';
import { 
  KnowledgeMapEmptyList, 
  ReactCsvDownloadWrapper, 
  ActionsRightWrapper,
  DownloadKnowledgeMapLink,
} from './styles';

const KnowledgePage = ({ currentUser, toast, getWorkspaceList }) => {
  const [isImportModalVisible, setImportModalVisible] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [isDuplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [isReplaceModalVisible, setReplaceModalVisible] = useState(false);
  const [toBeDeletedMap, setToBeDeletedMap] = useState(null);
  const [toBeDuplicatedMap, setToBeDuplicatedMap] = useState(null);
  const [toBeDuplicatedWorkspaceID,setToBeDuplicatedWorkspaceId] = useState(null);
  const [kmDocs, setkmDocs] = useState([]);
  const [toBeReplacedKnowledgeMap, setToBeReplacedKnowledgeMap] = useState({});

  const { apolloClient } = useContext(mercuryContext);
  useEffect(() => {
    if (location) {
      const { pathname } = location;
      gtmService.pushTelemetryPageActivityAndView({ pageName: pathname });
    }
  }, []);

  useEffect(() => {
    //for populating workspaceList in store on first load.
    if(Object.keys(WorkspaceService.getSelectedWorkspace()).length === 0) {
      getWorkspaceList();
    }
  }, [getWorkspaceList]);

  const downloadIndividualCSVRef = useRef();

  const downloadDocumentRef = useRef();

  const downloadCsvRef = useRef();
  
  const [knowledgeMapCSVContent, setKnowledgeMapCSVContent] = useState({fileName: KnowledgeMapFilenamePrefix,
    competencyList: []});

  useEffect(() => {
    if(knowledgeMapCSVContent && knowledgeMapCSVContent.competencyList.length > 0) downloadIndividualCSVRef.current.handleClick();
  }, [knowledgeMapCSVContent]);

  useEffect(()=>{
    const createDuplicateKnowledgeMap = async () => {
      const { id, title } = toBeDuplicatedMap;
      const workspaceId = toBeDuplicatedWorkspaceID;
      const originalKnowledgeMap = await getKnowledgeMapDetail(workspaceId, id, apolloClient);
      const { items } = simplifyDocumentQueryResult(originalKnowledgeMap.data);
      const newTitle = `${intl.formatMessage(messages.copyOf)+title}`;
      const { documentId } = await createKnowledgeMap(workspaceId, newTitle, apolloClient);
      const insertedKnowledgeMapItem = await insertKnowledgeMapItems(items, documentId, apolloClient);
      insertKnowledgeMapAssociation(insertedKnowledgeMapItem, documentId, apolloClient);
      setToBeDuplicatedWorkspaceId(null);
      setDuplicateModalVisible(false);
      dispatch(pushToast({
        message: intl.formatMessage(messages.duplicateSuccess),
      }));
    };

    if(toBeDuplicatedWorkspaceID){
      createDuplicateKnowledgeMap();
    }
  },[toBeDuplicatedWorkspaceID, toBeDuplicatedMap, apolloClient]);

  const onOpenModal = (type) => {
    if (type === KnowledgeTypes.IMPORT) {
      setImportModalVisible(true);
    }
  };
  //check for boundary cases
  const setToBeReplacedKnowledgeMapItems = async (documentId) => {
    const workspaceId = WorkspaceService.getSelectedWorkspaceId();
    const knowledgeMap = await getKnowledgeMapDetail(workspaceId, documentId, apolloClient);
    const simplifiedKnowledgeMap = simplifyDocumentQueryResult(knowledgeMap.data);
    const updatedKnowledgeMap = updateKnowledgeMapItemTitle(simplifiedKnowledgeMap);
    updatedKnowledgeMap.items && setToBeReplacedKnowledgeMap({
      documentId: documentId,
      items: updatedKnowledgeMap.items,
    });
  };

  const onOpenReplaceModal = (knowledgeItem) => {
    setToBeReplacedKnowledgeMapItems(knowledgeItem.id);
    setReplaceModalVisible(true);
  };
  
  const onCloseReplaceModal = () => {
    setReplaceModalVisible(false);
  };

  const getKnowledgeMapContent = (documentId) => async () => {
    const workspaceId = WorkspaceService.getSelectedWorkspaceId();
    const knowledgeMap = await getKnowledgeMapDetail(workspaceId, documentId, apolloClient);
    const simplifiedKnowledgeMap = simplifyDocumentQueryResult(knowledgeMap.data);
    const updatedKnowledgeMap = updateKnowledgeMapItemTitle(simplifiedKnowledgeMap);
    const competencyList = getCompetencyList(updatedKnowledgeMap);
    const knowledgeMapFileName = getKnowledgeMapFileName(updatedKnowledgeMap);
    setKnowledgeMapCSVContent({
      fileName: knowledgeMapFileName,
      competencyList: competencyList,
    });
  };

  const closeImportModal = () => {
    setImportModalVisible(false);
  };
  
  const showDeleteConfirmationDialog = (knowledgeMap) => {
    setDeleteModalVisible(true);
    setToBeDeletedMap(knowledgeMap);
  };

  const handleActivityDelete = async () => {
    const workspaceId = WorkspaceService.getSelectedWorkspaceId();
    const toBeDeletedDocumentId = toBeDeletedMap.id;
    await deleteKnowledgeMap(workspaceId, toBeDeletedDocumentId, toast, apolloClient);
    setDeleteModalVisible(false);
  };

  const handleDocsDeleteOption = async (data) => {
    const docs = simplifyKnowledgeMapQueryResult(data) || [];
    const docsWithDeleteOption = await addDeleteProperty(docs, currentUser.primaryEmail);
    setkmDocs(docsWithDeleteOption);
  };

  const onClickDuplicate = (knowledgeMap) => {
    setToBeDuplicatedMap(knowledgeMap);
    setDuplicateModalVisible(true);
  };

  const onDownloadModal = (downloadType) => {
    if(downloadType === KnowledgeMapDownloadOptions.TEMPLATE_DOWNLOAD) {
      downloadCsvRef?.current?.click();
    } else {
      downloadDocumentRef?.current?.click();
    }
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>{intl.formatMessage(messages.knowledge)}</title>
      </Helmet>

      <Sidebar navigation={<HomeNavigation selectedKey='knowledge' />} />

      <PageViewport style={{ position: 'relative' }}>
        <WorkspaceHeader title={intl.formatMessage(messages.knowledge)} />
        <PageActions>
          <PageActionsRight>
            <ActionsRightWrapper>
              <KnowledgeMapDownload openModalSelector={onDownloadModal}/>
              <AddKnowledgeMap openModalSelector={onOpenModal} />
            </ActionsRightWrapper>
          </PageActionsRight>
        </PageActions>
        <PageContent>
          <ReactCsvDownloadWrapper>
            <CSVDownloader
              ref={downloadIndividualCSVRef}
              filename={knowledgeMapCSVContent.fileName}
              datas={knowledgeMapCSVContent.competencyList}
              columns={CSVHeaderNames}
              wrapColumnChar={'"'}
            />
          </ReactCsvDownloadWrapper>
          <DownloadKnowledgeMapLink
            href={knowledgeMapDocumentationPdfPath}
            download={knowledgeMapPdfDocumentationFileName}
          > 
            <Button ref={downloadDocumentRef} />
          </DownloadKnowledgeMapLink>
          <DownloadKnowledgeMapLink
            href={knowledgeMapCsvTemplatePath}
            download={KnowledgeMapCsvTemplateFileName}
          > 
            <Button ref={downloadCsvRef} />
          </DownloadKnowledgeMapLink>
          <Query
            fetchPolicy='network-first'
            variables={{
              workspaceId: WorkspaceService.getSelectedWorkspaceId(),
            }}
            query={getDocumentsList}
            notifyOnNetworkStatusChange={true}
            onCompleted={handleDocsDeleteOption}
          >
            {({ loading, error }) => {
              if (error) {
                // eslint-disable-next-line no-console
                console.error(error);
                return null;
              }

              if (loading) {
                return <PlaceholderList />;
              }

              if (kmDocs.length === 0) {
                return <KnowledgeMapEmptyList />;
              }

              return (
                <KnowledgeMapsList
                  list={kmDocs}
                  downloadKnowledgeMap={getKnowledgeMapContent}
                  onDeleteOfKnowledgeMap={showDeleteConfirmationDialog}
                  duplicateKnowledgeMap={onClickDuplicate}
                  onReplaceKnowledgeMap={onOpenReplaceModal}
                />
              );
            }}
          </Query>
        </PageContent>
      </PageViewport>
      {isImportModalVisible &&
        <KnowledgeMapImportModal
          visible={isImportModalVisible}
          onDismiss={closeImportModal}
          toast={toast}
        />
      }
      {isReplaceModalVisible &&
        <KnowledgeMapImportModal
          visible={isReplaceModalVisible}
          onDismiss={onCloseReplaceModal}
          toast={toast}
          isReplace={isReplaceModalVisible}
          toBeReplacedKnowledgeMap={toBeReplacedKnowledgeMap}
        />
      }

      <CourseSelectorModal
        onPrimaryHandler={(workspace) => {
          setToBeDuplicatedWorkspaceId(workspace.id);
        }}
        onDismiss={() => {
          setDuplicateModalVisible(false);
        }}
        visible={isDuplicateModalVisible}
        primaryButtonText={intl.formatMessage(messages.selectButton)}
        selectWorkspaceOnly
      />

      <KnowledgeMapDeleteConfirm
        visible={isDeleteModalVisible}
        onDismiss={() => {
          setDeleteModalVisible(false);
        }}
        toBeDeletedDocumentName={toBeDeletedMap?.title}
        handleActivityDelete={handleActivityDelete}
      />

      <HelpMenu helpContext={helpContext.KNOWLEDGE} />
    </PageWrapper>
  );
};

KnowledgePage.propTypes = {
  toast: PropTypes.func,
  getWorkspaceList: PropTypes.func,
};

const mapStateToProps = (state) => ({
  currentUser: selectCurrentMe(state),
});

const mapDispatchToProps = (dispatch) => ({
  toast: () => (...args) => dispatch(pushToast(...args)),
  getWorkspaceList: () => dispatch(commandWorkspaceWorkspacesList()),
});

export default connect(mapStateToProps, mapDispatchToProps)(KnowledgePage);
